/**
 * Full-text search.
 *
 * Every hit arrives with its spans already resolved server-side, so the row shows the
 * exact matched characters rather than a re-derived guess. Clicking a hit fills the
 * evidence panel — the same panel a chat citation or a table cell fills.
 */

import { useState, type FormEvent } from "react";
import { Marked } from "../components/Marked";
import { api, type Hit } from "../lib/api";
import { describeLocator } from "../lib/locator";
import { useEvidence } from "../state/evidence";

export function Search({ datasetId }: { datasetId: string }) {
  const { select, selected } = useEvidence();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState<"fulltext" | "substring">("fulltext");
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setHits(await api.search(datasetId, q, mode));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <h1>Search</h1>

      <form onSubmit={run} className="row" data-testid="search-form">
        <input
          aria-label="Search"
          placeholder="Search this dataset"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          required
        />
        <select
          aria-label="Search mode"
          value={mode}
          onChange={(e) => setMode(e.target.value as "fulltext" | "substring")}
          style={{ width: "auto" }}
        >
          <option value="fulltext">stemmed</option>
          <option value="substring">exact substring</option>
        </select>
        <button className="primary" type="submit" disabled={busy}>
          {busy ? "…" : "Search"}
        </button>
      </form>

      <p className="mono" style={{ color: "var(--ink-faint)" }}>
        {mode === "fulltext"
          ? "Stemmed: matches inflected forms. Русский and English both."
          : "Exact substring: no stemming. A single Ukrainian word also widens to its " +
            "folded stem when the dataset's language is set — a folded hit is tagged " +
            "below."}
      </p>

      {error && (
        <p data-testid="search-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {hits && (
        <p className="eyebrow" data-testid="hit-count">
          {hits.length} {hits.length === 1 ? "hit" : "hits"}
        </p>
      )}

      <ol className="hits" data-testid="hit-list">
        {hits?.map((hit) => (
          <li key={hit.block_id}>
            <button
              className="hit"
              data-testid="hit"
              aria-current={selected?.blockId === hit.block_id}
              onClick={() =>
                select({
                  blockId: hit.block_id,
                  spans: hit.spans,
                  label: hit.original_name,
                  origin: "search",
                })
              }
            >
              <span className="eyebrow">
                {hit.original_name} · {describeLocator(hit.locator)}
                {hit.matched_by === "folded" && (
                  <span
                    className="tag"
                    data-testid="folded-match-tag"
                    title="Matched a folded Ukrainian stem, not the literal query — the highlighted characters are what actually matched."
                  >
                    {" "}
                    · folded match
                  </span>
                )}
              </span>
              <span className="hit-text">
                <Marked text={hit.text} spans={hit.spans} around={90} />
              </span>
            </button>
          </li>
        ))}
      </ol>

      {hits?.length === 0 && (
        <p data-testid="search-empty" style={{ color: "var(--ink-faint)" }}>
          Nothing matched. Try the exact-substring mode if you are looking for a
          fragment, an identifier, or a homoglyph.
        </p>
      )}
    </div>
  );
}
